import { phoneNumbersMatch } from '@poseidon/shared-utils/phoneNumbersMatch';

import type { JustCallCallEvent, JustCallDialerBridge } from './justcallDialerBridge';
import type { SoftphoneCallHandlers, SoftphoneClient, SoftphoneDialParams } from './softphoneClient.types';
import { getJustCallDialerBridge } from './justcallDialerBridge';

/** How long JustCall's dialer gets to load before we call the attempt failed. */
const READY_TIMEOUT_MS = 30_000;
/** How long to wait for an answer to "is the rep signed in?". */
const LOGIN_CHECK_TIMEOUT_MS = 3_000;
/**
 * JustCall's own docs disagree on whether a dial request starts the call or only fills in the
 * number, so when nothing has rung by now, tell the rep where the Call button is.
 */
const NOT_RINGING_HINT_MS = 6_000;

export const JUSTCALL_SIGN_IN_ERROR = 'Sign in to JustCall in the JustCall panel, then press Call again.';
export const JUSTCALL_PRESS_CALL_NOTICE = 'If the call hasn’t started, press Call in the JustCall panel.';

/**
 * JustCall implementation of the softphone contract. JustCall's dialer (an iframe, see
 * `justcallDialerBridge`) places and controls the call — this client hands it the number, follows
 * its call events to drive our status line, and reports JustCall's call id so the server can pull
 * the result (`onProviderCall` → `voiceCall.reportProviderCall`).
 *
 * Mute, keypad and hang-up belong to JustCall's dialer (`embeddedCarrierDialer`), so the dialer UI
 * hides ours: `setMuted` / `sendDigits` do nothing, and our "hang up" becomes "the call is over —
 * log it" (see `hangup`). Closing our dialer stops following the call; the call itself carries on
 * in JustCall until the rep hangs up there.
 */
export class JustCallSoftphoneClient implements SoftphoneClient {
  readonly embeddedCarrierDialer = true;

  private handlers: SoftphoneCallHandlers | null = null;
  private readonly bridge: JustCallDialerBridge;
  private unsubscribe: (() => void) | null = null;
  /** The call we're following: set by `dial`, cleared when it ends or the rep closes our dialer. */
  private pending: { destinationNumber: string } | null = null;
  private callSid: string | null = null;
  private hintTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(bridge: JustCallDialerBridge = getJustCallDialerBridge()) {
    this.bridge = bridge;
  }

  on(handlers: SoftphoneCallHandlers): void {
    this.handlers = handlers;
  }

  /**
   * Open JustCall's dialer and make sure the rep is signed in. The token is unused (see the server's
   * JustCallVoiceProvider). A signed-out rep is told to sign in and call again rather than waited
   * for: signing in can take minutes, and a call held open that long could end up dialing whoever
   * the page has moved on to by then.
   */
  async init(): Promise<void> {
    this.unsubscribe ??= this.bridge.onCallEvent((event) => this.handleCallEvent(event));
    this.bridge.activate({ expand: true });
    try {
      await this.bridge.whenReady(READY_TIMEOUT_MS);
    } catch {
      throw new Error('The JustCall dialer didn’t load. Check your connection and try again.');
    }
    if ((await this.bridge.checkLoggedIn(LOGIN_CHECK_TIMEOUT_MS)) === false) throw new Error(JUSTCALL_SIGN_IN_ERROR);
  }

  dial(params: SoftphoneDialParams): Promise<void> {
    this.stopFollowing();
    this.pending = { destinationNumber: params.destinationNumber };
    this.handlers?.onStatus('connecting');
    this.bridge.expand();
    this.bridge.dial(params.destinationNumber);
    this.hintTimer = setTimeout(() => {
      this.hintTimer = null;
      if (this.pending != null && this.callSid == null) this.handlers?.onNotice?.(JUSTCALL_PRESS_CALL_NOTICE);
    }, NOT_RINGING_HINT_MS);
    return Promise.resolve();
  }

  /**
   * JustCall's dialer owns the actual hang-up. Ours means "this call is over": stop following it and
   * move to the outcome step — the rep's way forward when JustCall never reports the end (its dial
   * request only filled the number in, or the rep called someone else there).
   */
  hangup(): void {
    if (this.pending != null) this.finishCall();
  }

  setMuted(): void {
    // JustCall's dialer owns mute — the dialer UI hides ours (`embeddedCarrierDialer`).
  }

  sendDigits(): void {
    // JustCall's dialer owns the keypad — the dialer UI hides ours (`embeddedCarrierDialer`).
  }

  closeCall(): void {
    this.stopFollowing();
  }

  destroy(): Promise<void> {
    this.stopFollowing();
    this.unsubscribe?.();
    this.unsubscribe = null;
    return Promise.resolve();
  }

  private stopFollowing(): void {
    this.pending = null;
    this.callSid = null;
    if (this.hintTimer != null) {
      clearTimeout(this.hintTimer);
      this.hintTimer = null;
    }
  }

  /** The call is over: stop following it, fold the dock away, and hand over to the outcome step. */
  private finishCall(): void {
    const callSid = this.callSid;
    this.stopFollowing();
    // Fold the dialer away so our outcome picker isn't hidden behind it.
    this.bridge.collapse();
    this.handlers?.onStatus('ended');
    if (callSid != null) this.handlers?.onProviderCall?.({ providerCallId: callSid, ended: true });
  }

  private handleCallEvent(event: JustCallCallEvent): void {
    const pending = this.pending;
    if (pending == null || event.direction === 'incoming') return;

    if (this.callSid == null) {
      // Only a ringing event says which number is being called, so only it can claim a call as ours:
      // an answered/ended event before it may belong to another call already live in JustCall. A
      // ring to a different number is the rep calling someone else there.
      if (event.name !== 'call-ringing' || event.callSid == null) return;
      if (event.to != null && !phoneNumbersMatch(event.to, pending.destinationNumber)) return;
      this.callSid = event.callSid;
      if (this.hintTimer != null) {
        clearTimeout(this.hintTimer);
        this.hintTimer = null;
      }
      this.handlers?.onNotice?.(null);
      this.handlers?.onProviderCall?.({ providerCallId: event.callSid, ended: false });
    } else if (event.callSid != null && event.callSid !== this.callSid) {
      return;
    }

    if (event.name === 'call-ringing') {
      this.handlers?.onStatus('ringing');
    } else if (event.name === 'call-answered') {
      this.handlers?.onStatus('active');
    } else {
      this.finishCall();
    }
  }
}
